import { ReactNode, useState, useEffect, useMemo } from 'react';
import { Alert, Badge, Button, Card, Collapse, Dropdown, Form } from 'react-bootstrap';
import { Funnel } from 'react-bootstrap-icons';
import { ProgressTracker } from '@/components/tools/ProgressTracker';
import { GroupedClientSelector } from '@/components/tools/GroupedClientSelector';
import { countOf, getSelectionDisplayText, reasonOf } from '@/components/tools/clientSelectionUtils';
import {
  IdCell,
  ReportChoices,
  ReportDownloads,
  ReportFailures,
  ReportStats,
  ReportTable,
  ReportTabs,
  type ReportColumn,
  type ReportStat,
} from '@/components/tools/ReportParts';
import { ToolPage } from '@/components/ui/ToolPage';
import { useToolExecution } from '@/hooks/useToolExecution';
import { UsageReportService } from '@/services/usageReportService';
import { clientService } from '@/services/clientService';
import { clientMetadataService } from '@/services/clientMetadataService';
import { formatNzDateTime } from '@/utils/relativeTime';
import type { Client, ClientMetadata } from '@/types';
import { CONTRACT_DISPLAY, CONTRACT_VALUES, isContract, resolveContract, trialExpired } from '@/config/contract';
import type {
  AgentRecord,
  AgentUsageRecord,
  AppRunRecord,
  ChatMessageRecord,
  IntegrationRecord,
  IntegrationChatUsageRecord,
  AgentIntegrationUsageRecord,
  KnowledgeBaseRecord,
  ToolResult,
  ToolResultFile,
  UsageReportParameters,
  UsageReportResult,
  UsageReportType,
  UsageSummary,
} from '@/types/tools';
import './UsageReportTool.css';

const DEFAULT_REPORT_TYPES: UsageReportType[] = [
  'summary',
  'app-runs',
  'chat-messages',
  'agents',
  'agent-usage',
  'scheduled-agent-usage',
  'integrations',
  'integration-chat-usage',
  'agent-integration-usage',
  'knowledge-bases',
];

const REPORT_OPTIONS: { value: UsageReportType; label: string; description: string }[] = [
  {
    value: 'summary',
    label: 'Summary by user',
    description: 'App runs and chat messages for each user, in each month or day.',
  },
  { value: 'app-runs', label: 'App runs', description: 'Every app run, one row each.' },
  { value: 'chat-messages', label: 'Chat messages', description: 'Every chat message, tool events included.' },
  { value: 'agents', label: 'Agents', description: 'Every shared and personal agent, with who made it.' },
  {
    value: 'agent-usage',
    label: 'Agent usage',
    description: 'Conversations people started with an agent. Scheduled runs are left out.',
  },
  {
    value: 'scheduled-agent-usage',
    label: 'Scheduled agent runs',
    description: 'Agent conversations a schedule started.',
  },
  {
    value: 'integrations',
    label: 'Integrations',
    description: "Each client's integrations, their state and the tools an admin blocked.",
  },
  {
    value: 'integration-chat-usage',
    label: 'Chat with integrations',
    description: "Each time chat used an integration's tool.",
  },
  {
    value: 'agent-integration-usage',
    label: 'Agents with integrations',
    description: "Agent conversations that used an integration's tool.",
  },
  {
    value: 'knowledge-bases',
    label: 'Numa Files',
    description: "Each client's Numa Files folders, with the number of files.",
  },
];

const REPORT_LABEL = Object.fromEntries(REPORT_OPTIONS.map((r) => [r.value, r.label])) as Record<
  UsageReportType,
  string
>;

const DEFAULT_PARAMETERS: UsageReportParameters = {
  clientNames: [],
  timePeriod: 'current-year',
  customStartDate: '',
  customEndDate: '',
  timeGranularity: 'month',
  outputFormat: 'json',
  reports: DEFAULT_REPORT_TYPES,
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-07-01" as "1 Jul 2026". A calendar date the person chose, so no time zone shifts it. */
const formatDay = (day: string): string => {
  const [y, m, d] = day.split('-').map(Number);
  return y && m && d ? `${d} ${MONTHS[m - 1]} ${y}` : day;
};

/** A time from the export, in New Zealand time. A value that is not a time is shown as sent. */
const nzTime = (iso?: string): ReactNode => (iso ? <span title={iso}>{formatNzDateTime(iso) || iso}</span> : null);

/** A set of counts by name, such as runs by app: "invoices 12, leave 3". */
const countsByName = (counts?: Record<string, number>): string =>
  Object.entries(counts ?? {})
    .map(([name, n]) => `${name} ${n.toLocaleString()}`)
    .join(', ');

const renderStatusBadge = (status?: string) => {
  if (!status) return null;
  if (!isContract(status)) return status;
  const display = CONTRACT_DISPLAY[status];
  // Solid purple is kept for the one "current" marker on a page, so Internal reads as a plain pill.
  return <Badge bg={display.variant === 'primary' ? 'secondary' : display.variant}>{display.label}</Badge>;
};

/** The two columns every table starts with. */
const clientColumns = <T extends { clientName: string; clientStatus?: string }>(): ReportColumn<T>[] => [
  { key: 'client', header: 'Client', className: 'usage-client', cell: (row) => row.clientName },
  { key: 'clientStatus', header: 'Contract', cell: (row) => renderStatusBadge(row.clientStatus) },
];

const SUMMARY_COLUMNS: ReportColumn<UsageSummary>[] = [
  ...clientColumns<UsageSummary>(),
  { key: 'userId', header: 'User ID', cell: (row) => <IdCell value={row.userId} label="Copy user ID" /> },
  { key: 'userEmail', header: 'User email', cell: (row) => row.userEmail },
  { key: 'period', header: 'Period', className: 'mono', cell: (row) => row.month },
  { key: 'appRuns', header: 'App runs', kind: 'number', cell: (row) => row.appRuns.toLocaleString() },
  { key: 'chatMessages', header: 'Chat messages', kind: 'number', cell: (row) => row.chatMessages.toLocaleString() },
  { key: 'byApp', header: 'App runs by app', kind: 'wrap', cell: (row) => countsByName(row.appRunsByApp) },
  {
    key: 'byType',
    header: 'Chat messages by type',
    kind: 'wrap',
    cell: (row) => countsByName(row.chatMessagesByType),
  },
];

const APP_RUN_COLUMNS: ReportColumn<AppRunRecord>[] = [
  ...clientColumns<AppRunRecord>(),
  { key: 'userId', header: 'User ID', cell: (row) => <IdCell value={row.userId} label="Copy user ID" /> },
  { key: 'userEmail', header: 'User email', cell: (row) => row.userEmail },
  { key: 'appId', header: 'App ID', cell: (row) => <IdCell value={row.appId} label="Copy app ID" /> },
  { key: 'appName', header: 'App', cell: (row) => row.appName },
  { key: 'period', header: 'Period', className: 'mono', cell: (row) => row.month },
  { key: 'jobId', header: 'Job ID', cell: (row) => <IdCell value={row.jobId} label="Copy job ID" /> },
  { key: 'startedAt', header: 'Started', title: 'New Zealand time', cell: (row) => nzTime(row.startedAt) },
  { key: 'status', header: 'Result', cell: (row) => row.status },
];

const CHAT_COLUMNS: ReportColumn<ChatMessageRecord>[] = [
  ...clientColumns<ChatMessageRecord>(),
  { key: 'userId', header: 'User ID', cell: (row) => <IdCell value={row.userId} label="Copy user ID" /> },
  { key: 'userEmail', header: 'User email', cell: (row) => row.userEmail },
  { key: 'period', header: 'Period', className: 'mono', cell: (row) => row.month },
  {
    key: 'conversationId',
    header: 'Conversation ID',
    cell: (row) => <IdCell value={row.conversationId} label="Copy conversation ID" />,
  },
  { key: 'messageType', header: 'Message type', cell: (row) => row.messageType },
  { key: 'role', header: 'Role', cell: (row) => row.role },
  { key: 'timestamp', header: 'Sent', title: 'New Zealand time', cell: (row) => nzTime(row.timestamp) },
];

const AGENT_COLUMNS: ReportColumn<AgentRecord>[] = [
  ...clientColumns<AgentRecord>(),
  { key: 'agentId', header: 'Agent ID', cell: (row) => <IdCell value={row.agentId} label="Copy agent ID" /> },
  { key: 'agentName', header: 'Agent', cell: (row) => row.agentName },
  { key: 'visibility', header: 'Visibility', cell: (row) => row.visibility },
  { key: 'agentType', header: 'Agent type', cell: (row) => row.agentType },
  { key: 'createdBy', header: 'Made by (ID)', cell: (row) => <IdCell value={row.createdBy} label="Copy user ID" /> },
  { key: 'createdByEmail', header: 'Made by (email)', cell: (row) => row.createdByEmail },
  { key: 'createdAt', header: 'Made', title: 'New Zealand time', cell: (row) => nzTime(row.createdAt) },
  { key: 'scope', header: 'Scope', cell: (row) => row.scope },
];

const AGENT_USAGE_COLUMNS: ReportColumn<AgentUsageRecord>[] = [
  ...clientColumns<AgentUsageRecord>(),
  { key: 'agentId', header: 'Agent ID', cell: (row) => <IdCell value={row.agentId} label="Copy agent ID" /> },
  { key: 'agentName', header: 'Agent', cell: (row) => row.agentName },
  { key: 'userId', header: 'User ID', cell: (row) => <IdCell value={row.userId} label="Copy user ID" /> },
  { key: 'userEmail', header: 'User email', cell: (row) => row.userEmail },
  { key: 'period', header: 'Period', className: 'mono', cell: (row) => row.month },
  {
    key: 'conversations',
    header: 'Conversations',
    kind: 'number',
    cell: (row) => row.conversationCount.toLocaleString(),
  },
  { key: 'visibility', header: 'Visibility', cell: (row) => row.visibility },
  { key: 'agentType', header: 'Agent type', cell: (row) => row.agentType },
];

const INTEGRATION_COLUMNS: ReportColumn<IntegrationRecord>[] = [
  ...clientColumns<IntegrationRecord>(),
  { key: 'integration', header: 'Integration', cell: (row) => row.integration },
  { key: 'status', header: 'State', cell: (row) => row.status },
  { key: 'denyTools', header: 'Blocked tools', kind: 'wrap', cell: (row) => row.denyTools?.join(', ') },
  { key: 'updatedAt', header: 'Changed', title: 'New Zealand time', cell: (row) => nzTime(row.updatedAt) },
  { key: 'updatedBy', header: 'Changed by', cell: (row) => row.updatedBy },
];

const INTEGRATION_CHAT_COLUMNS: ReportColumn<IntegrationChatUsageRecord>[] = [
  ...clientColumns<IntegrationChatUsageRecord>(),
  { key: 'integration', header: 'Integration', cell: (row) => row.integration },
  { key: 'userId', header: 'User ID', cell: (row) => <IdCell value={row.userId} label="Copy user ID" /> },
  { key: 'userEmail', header: 'User email', cell: (row) => row.userEmail },
  {
    key: 'conversationId',
    header: 'Conversation ID',
    cell: (row) => <IdCell value={row.conversationId} label="Copy conversation ID" />,
  },
  { key: 'period', header: 'Period', className: 'mono', cell: (row) => row.month },
  { key: 'toolName', header: 'Tool', className: 'mono', cell: (row) => row.toolName },
];

const AGENT_INTEGRATION_COLUMNS: ReportColumn<AgentIntegrationUsageRecord>[] = [
  ...clientColumns<AgentIntegrationUsageRecord>(),
  { key: 'integration', header: 'Integration', cell: (row) => row.integration },
  { key: 'agentId', header: 'Agent ID', cell: (row) => <IdCell value={row.agentId} label="Copy agent ID" /> },
  { key: 'agentName', header: 'Agent', cell: (row) => row.agentName },
  { key: 'userId', header: 'User ID', cell: (row) => <IdCell value={row.userId} label="Copy user ID" /> },
  { key: 'userEmail', header: 'User email', cell: (row) => row.userEmail },
  {
    key: 'conversationId',
    header: 'Conversation ID',
    cell: (row) => <IdCell value={row.conversationId} label="Copy conversation ID" />,
  },
  { key: 'period', header: 'Period', className: 'mono', cell: (row) => row.month },
  { key: 'toolName', header: 'Tool', className: 'mono', cell: (row) => row.toolName },
];

const KNOWLEDGE_BASE_COLUMNS: ReportColumn<KnowledgeBaseRecord>[] = [
  ...clientColumns<KnowledgeBaseRecord>(),
  { key: 'kbType', header: 'Index type', className: 'mono', cell: (row) => row.kbType },
  { key: 'scope', header: 'Scope', cell: (row) => row.scope },
  { key: 'name', header: 'Folder', cell: (row) => row.name },
  { key: 'kbId', header: 'Folder ID', cell: (row) => <IdCell value={row.kbId} label="Copy folder ID" /> },
  { key: 'bucket', header: 'Bucket', className: 'mono', cell: (row) => row.bucket },
  { key: 'prefix', header: 'Path', className: 'mono', cell: (row) => row.prefix },
  { key: 'fileCount', header: 'Files', kind: 'number', cell: (row) => row.fileCount.toLocaleString() },
  { key: 'createdBy', header: 'Made by', cell: (row) => row.createdBy },
  { key: 'notes', header: 'Notes', kind: 'wrap', cell: (row) => row.notes },
];

export default function UsageReportTool() {
  const [parameters, setParameters] = useState<UsageReportParameters>(DEFAULT_PARAMETERS);
  const [clients, setClients] = useState<Client[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [clientsError, setClientsError] = useState<string | null>(null);
  const [resultFiles, setResultFiles] = useState<ToolResultFile[]>([]);
  const [activeTab, setActiveTab] = useState<string>('summary');
  // Separate state for month inputs to allow free editing
  const [startMonthInput, setStartMonthInput] = useState('');
  const [endMonthInput, setEndMonthInput] = useState('');
  // Client filter state
  const [metadataMap, setMetadataMap] = useState<Map<string, ClientMetadata>>(new Map());
  const [statusFilters, setStatusFilters] = useState<string[]>([]);
  const [trialStartFrom, setTrialStartFrom] = useState('');
  const [trialStartTo, setTrialStartTo] = useState('');
  const [trialEndFrom, setTrialEndFrom] = useState('');
  const [trialEndTo, setTrialEndTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const { execution, isRunning, execute, cancel, reset } = useToolExecution({
    onCompleted: (result) => {
      if (result.files) {
        setResultFiles(result.files);
      }
    },
    onFailed: (error) => {
      console.error('Tool execution failed:', error);
    },
  });

  const report = execution?.result?.data as UsageReportResult | undefined;

  // Load clients on component mount
  useEffect(() => {
    loadClients();
  }, []);

  useEffect(() => {
    const reportsFromResult = report?.metadata?.selectedReports || [];
    if (execution?.status === 'completed' && reportsFromResult.length > 0) {
      if (!reportsFromResult.includes(activeTab as UsageReportType)) {
        setActiveTab(reportsFromResult[0]);
      }
    }
  }, [execution, activeTab]);

  const loadClients = async () => {
    setLoadingClients(true);
    setClientsError(null);
    try {
      const [allClients, metadata] = await Promise.all([
        clientService.getAllClients(),
        clientMetadataService.getAllMetadata(),
      ]);
      setClients(allClients);
      setMetadataMap(metadata);
    } catch (error) {
      console.error('Failed to load clients:', error);
      setClientsError(reasonOf(error));
    } finally {
      setLoadingClients(false);
    }
  };

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (statusFilters.length > 0) count++;
    if (trialStartFrom || trialStartTo) count++;
    if (trialEndFrom || trialEndTo) count++;
    return count;
  }, [statusFilters, trialStartFrom, trialStartTo, trialEndFrom, trialEndTo]);

  const clearFilters = () => {
    setStatusFilters([]);
    setTrialStartFrom('');
    setTrialStartTo('');
    setTrialEndFrom('');
    setTrialEndTo('');
  };

  const handleStatusToggle = (value: string) => {
    setStatusFilters((prev) => (prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value]));
  };

  const statusOptions: { value: string; label: string }[] = [
    ...CONTRACT_VALUES.map((c) => ({ value: c, label: CONTRACT_DISPLAY[c].label })),
    { value: 'expired', label: 'Trial, ended' },
    { value: 'unset', label: 'No contract set' },
  ];

  const filteredClients = useMemo(() => {
    let result = clients;

    if (statusFilters.length > 0) {
      result = result.filter((c) => {
        const meta = metadataMap.get(c.name);
        const contract = resolveContract(meta, c.config);
        return statusFilters.some((f) => {
          if (f === 'expired') return contract === 'trial' && trialExpired(meta);
          if (f === 'unset') return !contract;
          return contract === f;
        });
      });
    }

    if (trialStartFrom) {
      result = result.filter((c) => {
        const meta = metadataMap.get(c.name);
        return meta?.trialStartDate && meta.trialStartDate >= trialStartFrom;
      });
    }
    if (trialStartTo) {
      result = result.filter((c) => {
        const meta = metadataMap.get(c.name);
        return meta?.trialStartDate && meta.trialStartDate <= trialStartTo;
      });
    }

    if (trialEndFrom) {
      result = result.filter((c) => {
        const meta = metadataMap.get(c.name);
        return meta?.trialEndDate && meta.trialEndDate >= trialEndFrom;
      });
    }
    if (trialEndTo) {
      result = result.filter((c) => {
        const meta = metadataMap.get(c.name);
        return meta?.trialEndDate && meta.trialEndDate <= trialEndTo;
      });
    }

    return result;
  }, [clients, statusFilters, trialStartFrom, trialStartTo, trialEndFrom, trialEndTo, metadataMap]);

  // Remove selected clients that are no longer in the filtered list
  useEffect(() => {
    const filteredNames = new Set(filteredClients.map((c) => c.name));
    const validSelections = parameters.clientNames.filter((n) => filteredNames.has(n));
    if (validSelections.length !== parameters.clientNames.length) {
      setParameters((prev) => ({ ...prev, clientNames: validSelections }));
    }
  }, [filteredClients]);

  const handleClientToggle = (clientName: string) => {
    setParameters((prev) => {
      const newClientNames = prev.clientNames.includes(clientName)
        ? prev.clientNames.filter((n) => n !== clientName)
        : [...prev.clientNames, clientName];
      return { ...prev, clientNames: newClientNames };
    });
  };

  const handleSelectClients = (clientNames: string[]) => {
    setParameters((prev) => ({ ...prev, clientNames }));
  };

  const handleReportToggle = (report: UsageReportType) => {
    setParameters((prev) => {
      const nextReports = prev.reports.includes(report)
        ? prev.reports.filter((r) => r !== report)
        : [...prev.reports, report];
      return { ...prev, reports: nextReports };
    });
  };

  const handleSelectAllReports = () => {
    setParameters((prev) => {
      return { ...prev, reports: REPORT_OPTIONS.map((r) => r.value) };
    });
  };

  const handleClearReports = () => {
    setParameters((prev) => ({ ...prev, reports: [] }));
  };

  const handleParameterChange = (field: keyof UsageReportParameters, value: string) => {
    setParameters((prev) => {
      if (field === 'timePeriod' && value !== 'custom') {
        return { ...prev, timePeriod: value, customStartDate: '', customEndDate: '' };
      }
      if (field === 'timeGranularity') {
        const nextPeriod =
          value === 'day' ? 'custom' : prev.timePeriod === 'custom' ? 'current-month' : prev.timePeriod;
        return { ...prev, timeGranularity: value as 'month' | 'day', timePeriod: nextPeriod };
      }
      return { ...prev, [field]: value };
    });
  };

  /**
   * Switches between counting by month and by day. Going back to months reads
   * the current month, so what was typed in the month boxes is cleared and the
   * boxes show the month the report will read.
   */
  const handleGranularityChange = (value: string) => {
    handleParameterChange('timeGranularity', value);
    setStartMonthInput('');
    setEndMonthInput('');
  };

  const handleExecute = async () => {
    if (parameters.timePeriod === 'custom' && !isCustomRangeValid) {
      return;
    }
    // If all clients are selected, pass empty array to indicate "all clients"
    const clientNamesToUse = parameters.clientNames.length === clients.length ? [] : parameters.clientNames;
    const paramsToUse = { ...parameters, clientNames: clientNamesToUse };
    if (parameters.reports.length > 0) {
      setActiveTab(parameters.reports[0]);
    }

    await execute('usage-report', paramsToUse as unknown as Record<string, unknown>, async (params, onProgress) => {
      const { result, files } = await UsageReportService.generateReport(
        params as unknown as UsageReportParameters,
        onProgress
      );

      return {
        type: 'file',
        files,
        data: result,
      } as ToolResult;
    });
  };

  /** Back to the form with the same choices. */
  const handleChangeChoices = () => {
    reset();
    setResultFiles([]);
  };

  /** Back to the form with every choice reset. */
  const handleReset = () => {
    reset();
    setResultFiles([]);
    setActiveTab('summary');
    setStartMonthInput('');
    setEndMonthInput('');
    clearFilters();
    setShowFilters(false);
    setParameters(DEFAULT_PARAMETERS);
  };

  const isCustomRange = parameters.timePeriod === 'custom';
  const isCustomRangeValid =
    !isCustomRange ||
    (!!parameters.customStartDate &&
      !!parameters.customEndDate &&
      parameters.customStartDate <= parameters.customEndDate);

  const isFormValid = () => {
    // For monthly granularity, also check month input validation
    const monthInputsValid = parameters.timeGranularity !== 'month' || !hasMonthValidationError;
    return (
      parameters.clientNames.length > 0 &&
      parameters.timePeriod &&
      (!isCustomRange || isCustomRangeValid) &&
      monthInputsValid &&
      !monthValidationError &&
      parameters.outputFormat &&
      parameters.reports.length > 0
    );
  };

  const currentMonthValue = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  };
  const previousMonthValue = () => {
    const now = new Date();
    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`;
  };
  const currentYear = new Date().getFullYear();
  const isValidMonth = (month: string) => /^\d{4}-\d{2}$/.test(month);
  const monthToStartDate = (month: string) => `${month}-01`;
  const monthToEndDate = (month: string) => {
    const [y, m] = month.split('-').map(Number);
    const end = new Date(y, m, 0); // last day of month
    return `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;
  };

  // The months a named period covers. The month boxes show them until the person
  // types their own, so the boxes always show what the report will read.
  const [namedStartMonth, namedEndMonth] =
    parameters.timePeriod === 'current-year'
      ? [`${currentYear}-01`, `${currentYear}-12`]
      : [currentMonthValue(), currentMonthValue()];

  // Get display values - use input state if set, otherwise derive from parameters
  const startMonthValue =
    startMonthInput ||
    (isCustomRange && parameters.customStartDate ? parameters.customStartDate.slice(0, 7) : namedStartMonth);
  const endMonthValue =
    endMonthInput ||
    (isCustomRange && parameters.customEndDate
      ? parameters.customEndDate.slice(0, 7)
      : startMonthInput
        ? startMonthValue
        : namedEndMonth);

  // Validation for month inputs
  const isStartMonthInputValid = !startMonthInput || isValidMonth(startMonthInput);
  const isEndMonthInputValid = !endMonthInput || isValidMonth(endMonthInput);
  const hasMonthValidationError = !isStartMonthInputValid || !isEndMonthInputValid;

  // Get validation error message for month inputs
  const getMonthValidationError = (): string | null => {
    if (startMonthInput && !isValidMonth(startMonthInput)) {
      return 'Write the start month as a year and a month, such as 2026-01.';
    }
    if (endMonthInput && !isValidMonth(endMonthInput)) {
      return 'Write the end month as a year and a month, such as 2026-12.';
    }
    if (
      parameters.customStartDate &&
      parameters.customEndDate &&
      parameters.customStartDate > parameters.customEndDate
    ) {
      return 'The start month must be the end month or a month before it.';
    }
    return null;
  };
  const monthValidationError = getMonthValidationError();

  // Handlers for month inputs - update input state immediately, sync to parameters only when valid
  const handleStartMonthChange = (value: string) => {
    setStartMonthInput(value);
    if (isValidMonth(value)) {
      setParameters((prev) => ({
        ...prev,
        timePeriod: 'custom',
        customStartDate: monthToStartDate(value),
        // If end is not set or is before start, also update end
        customEndDate:
          !prev.customEndDate || prev.customEndDate < monthToStartDate(value)
            ? monthToEndDate(value)
            : prev.customEndDate,
      }));
    }
  };

  const handleEndMonthChange = (value: string) => {
    setEndMonthInput(value);
    if (isValidMonth(value)) {
      setParameters((prev) => ({
        ...prev,
        timePeriod: 'custom',
        customEndDate: monthToEndDate(value),
      }));
    }
  };

  const setMonthRange = (startMonth: string, endMonth: string) => {
    setStartMonthInput(startMonth);
    setEndMonthInput(endMonth);
    if (isValidMonth(startMonth) && isValidMonth(endMonth)) {
      setParameters((prev) => ({
        ...prev,
        timePeriod: 'custom',
        customStartDate: monthToStartDate(startMonth),
        customEndDate: monthToEndDate(endMonth),
      }));
    }
  };

  /** The period the report reads, in words. */
  const getTimePeriodLabel = () => {
    if (isCustomRange && parameters.customStartDate && parameters.customEndDate) {
      return `${formatDay(parameters.customStartDate)} to ${formatDay(parameters.customEndDate)}`;
    }
    if (parameters.timePeriod === 'current-year') return `All of ${currentYear}`;
    if (parameters.timePeriod === 'current-month') return `This month (${currentMonthValue()})`;
    return parameters.timePeriod;
  };

  const getClientSelectionText = () => {
    return getSelectionDisplayText(clients, parameters.clientNames);
  };

  const formatReportList = (reports: UsageReportType[]) => {
    if (reports.length === 0) return 'No reports';
    if (reports.length === REPORT_OPTIONS.length) return `All reports (${REPORT_OPTIONS.length})`;
    if (reports.length === 1) return REPORT_LABEL[reports[0]] || reports[0];
    return `${reports.length} of ${REPORT_OPTIONS.length} reports`;
  };

  /** Why the report cannot run yet, or nothing when it can. */
  const missingChoice = (): string | null => {
    if (parameters.clientNames.length === 0) return 'Pick at least one client.';
    if (parameters.reports.length === 0) return 'Pick at least one report.';
    if (parameters.timeGranularity === 'day' && !isCustomRangeValid) {
      return 'Set a start date and an end date, with the start on or before the end.';
    }
    if (monthValidationError) return monthValidationError;
    return null;
  };
  const blocked = missingChoice();

  const buildStats = (metadata: UsageReportResult['metadata']): ReportStat[] => {
    const stats: ReportStat[] = [{ label: 'Clients read', value: metadata.clientsProcessed.toLocaleString() }];
    const add = (label: string, value?: number) => {
      if (value !== undefined) stats.push({ label, value: value.toLocaleString() });
    };
    add('App runs', metadata.totalAppRuns);
    add('Chat messages', metadata.totalChatMessages);
    add('People who used Numa', metadata.uniqueUsers);
    add('Agents', metadata.totalAgents);
    add('Agent conversations', metadata.agentConversationCount);
    add('Scheduled agent runs', metadata.scheduledAgentConversationCount);
    add('People who used an agent', metadata.uniqueAgentUsers);
    add('Integrations', metadata.totalIntegrations);
    add('Chat uses of an integration', metadata.totalIntegrationChats);
    add('Agent uses of an integration', metadata.totalAgentIntegrationRuns);
    add('Numa Files folders', metadata.totalKnowledgeBases);
    add('Files in those folders', metadata.totalKnowledgeBaseFiles);
    return stats;
  };

  const finished = execution?.status === 'completed';
  const selectedReportsFromResult = report?.metadata?.selectedReports || [];
  const failedClients = report?.failedClients || [];

  // One entry for each table the run made, in the order the tabs are shown.
  const table = <T,>(key: UsageReportType, rows: T[] | undefined, columns: ReportColumn<T>[], empty: string) => ({
    key,
    count: rows?.length ?? 0,
    render: (): ReactNode => <ReportTable key={key} columns={columns} rows={rows ?? []} empty={empty} />,
  });
  const tables = [
    table('summary', report?.summary, SUMMARY_COLUMNS, 'No app runs or chat messages in this period.'),
    table('app-runs', report?.appRuns, APP_RUN_COLUMNS, 'No app runs in this period.'),
    table('chat-messages', report?.chatMessages, CHAT_COLUMNS, 'No chat messages in this period.'),
    table('agents', report?.agents, AGENT_COLUMNS, 'These clients have no agents.'),
    table(
      'agent-usage',
      report?.agentUsage,
      AGENT_USAGE_COLUMNS,
      'Nobody started a conversation with an agent in this period.'
    ),
    table(
      'scheduled-agent-usage',
      report?.scheduledAgentUsage,
      AGENT_USAGE_COLUMNS,
      'No schedule ran an agent in this period.'
    ),
    table('integrations', report?.integrations, INTEGRATION_COLUMNS, 'These clients have no integrations set up.'),
    table(
      'integration-chat-usage',
      report?.integrationChatUsage,
      INTEGRATION_CHAT_COLUMNS,
      'Chat did not use an integration in this period.'
    ),
    table(
      'agent-integration-usage',
      report?.agentIntegrationUsage,
      AGENT_INTEGRATION_COLUMNS,
      'No agent used an integration in this period.'
    ),
    table(
      'knowledge-bases',
      report?.knowledgeBases,
      KNOWLEDGE_BASE_COLUMNS,
      'These clients have no Numa Files folders.'
    ),
  ].filter((entry) => selectedReportsFromResult.includes(entry.key));

  const shownTable = tables.find((entry) => entry.key === activeTab) || tables[0];
  const totalRows = tables.reduce((sum, entry) => sum + entry.count, 0);

  return (
    <ToolPage title="Usage report" subtitle="Export a client's app runs, chat messages and user activity for a period.">
      {clientsError && (
        <Alert variant="danger">
          The client list did not load, so there is nothing to pick from. {clientsError}{' '}
          <Button variant="link" className="report-inline-link" onClick={loadClients}>
            Try again
          </Button>
        </Alert>
      )}

      {!execution && (
        <Card className="report-form">
          <Card.Body>
            <div className="report-form__grid">
              <div className="report-form__col">
                <h2 className="section-label">Clients</h2>

                <div className="usage-filter-bar">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setShowFilters(!showFilters)}
                    aria-expanded={showFilters}
                    aria-controls="usage-client-filters"
                  >
                    <Funnel size={12} className="me-1" />
                    Narrow the list
                    {activeFilterCount > 0 && <span className="usage-filter-count">{activeFilterCount}</span>}
                  </Button>
                  {activeFilterCount > 0 && (
                    <>
                      <Button variant="outline-secondary" size="sm" onClick={clearFilters}>
                        Clear filters
                      </Button>
                      <span className="usage-filter-note">
                        {filteredClients.length} of {countOf(clients.length, 'client')} match
                      </span>
                    </>
                  )}
                </div>
                <Collapse in={showFilters}>
                  <div id="usage-client-filters">
                    <div className="usage-filters">
                      <Form.Group className="report-field">
                        <Form.Label>Contract</Form.Label>
                        <Dropdown autoClose="outside">
                          <Dropdown.Toggle variant="outline-secondary" size="sm" className="usage-filters__status">
                            {statusFilters.length === 0
                              ? 'Any contract'
                              : statusFilters.map((f) => statusOptions.find((o) => o.value === f)?.label).join(', ')}
                          </Dropdown.Toggle>
                          <Dropdown.Menu className="w-100">
                            {statusOptions.map((opt) => (
                              // The row takes the click, so the tick box only shows the state.
                              <Dropdown.Item key={opt.value} as="div" onClick={() => handleStatusToggle(opt.value)}>
                                <Form.Check
                                  type="checkbox"
                                  label={opt.label}
                                  checked={statusFilters.includes(opt.value)}
                                  readOnly
                                />
                              </Dropdown.Item>
                            ))}
                          </Dropdown.Menu>
                        </Dropdown>
                      </Form.Group>
                      <Form.Group className="report-field">
                        <Form.Label>Trial started between</Form.Label>
                        <div className="report-range">
                          <Form.Control
                            type="date"
                            size="sm"
                            aria-label="Trial started on or after"
                            value={trialStartFrom}
                            onChange={(e) => setTrialStartFrom(e.target.value)}
                          />
                          <span className="report-range__to">and</span>
                          <Form.Control
                            type="date"
                            size="sm"
                            aria-label="Trial started on or before"
                            value={trialStartTo}
                            min={trialStartFrom || undefined}
                            onChange={(e) => setTrialStartTo(e.target.value)}
                          />
                        </div>
                      </Form.Group>
                      <Form.Group className="report-field">
                        <Form.Label>Trial ends between</Form.Label>
                        <div className="report-range">
                          <Form.Control
                            type="date"
                            size="sm"
                            aria-label="Trial ends on or after"
                            value={trialEndFrom}
                            onChange={(e) => setTrialEndFrom(e.target.value)}
                          />
                          <span className="report-range__to">and</span>
                          <Form.Control
                            type="date"
                            size="sm"
                            aria-label="Trial ends on or before"
                            value={trialEndTo}
                            min={trialEndFrom || undefined}
                            onChange={(e) => setTrialEndTo(e.target.value)}
                          />
                        </div>
                        <Form.Text>Either date can be left empty. A client with no trial date is left out.</Form.Text>
                      </Form.Group>
                    </div>
                  </div>
                </Collapse>

                <GroupedClientSelector
                  clients={filteredClients}
                  selectedClientNames={parameters.clientNames}
                  onClientToggle={handleClientToggle}
                  onSelectClients={handleSelectClients}
                  disabled={isRunning}
                  loading={loadingClients}
                />
              </div>

              <div className="report-form__col">
                <h2 className="section-label">Reports</h2>
                <div className="usage-reports__head">
                  <Form.Check
                    type="checkbox"
                    id="select-all-reports"
                    label={
                      <span>
                        All reports
                        <span className="usage-reports__count">
                          {parameters.reports.length} of {REPORT_OPTIONS.length}
                        </span>
                      </span>
                    }
                    checked={parameters.reports.length === REPORT_OPTIONS.length}
                    ref={(el: HTMLInputElement | null) => {
                      if (el)
                        el.indeterminate =
                          parameters.reports.length > 0 && parameters.reports.length < REPORT_OPTIONS.length;
                    }}
                    onChange={handleSelectAllReports}
                    disabled={isRunning}
                  />
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={handleClearReports}
                    disabled={isRunning || parameters.reports.length === 0}
                  >
                    Clear
                  </Button>
                </div>
                <div className="usage-reports">
                  {REPORT_OPTIONS.map((option) => (
                    <Form.Check
                      key={option.value}
                      type="checkbox"
                      id={`report-${option.value}`}
                      className="usage-reports__item"
                      label={
                        <>
                          <span className="usage-reports__name">{option.label}</span>
                          <span className="usage-reports__desc">{option.description}</span>
                        </>
                      }
                      checked={parameters.reports.includes(option.value)}
                      onChange={() => handleReportToggle(option.value)}
                      disabled={isRunning}
                    />
                  ))}
                </div>
              </div>

              <div className="report-form__col">
                <h2 className="section-label">Period and files</h2>
                <Form.Group className="report-field" controlId="usage-granularity">
                  <Form.Label>Count by</Form.Label>
                  <Form.Select
                    value={parameters.timeGranularity || 'month'}
                    onChange={(e) => handleGranularityChange(e.target.value)}
                    disabled={isRunning}
                  >
                    <option value="month">Month</option>
                    <option value="day">Day</option>
                  </Form.Select>
                  <Form.Text>Each row of the export covers one month or one day.</Form.Text>
                </Form.Group>

                <Form.Group className="report-field">
                  <Form.Label>Period</Form.Label>
                  {parameters.timeGranularity === 'day' ? (
                    <>
                      <div className="report-range">
                        <Form.Control
                          type="date"
                          aria-label="Start date"
                          value={parameters.customStartDate}
                          onChange={(e) =>
                            setParameters((prev) => ({
                              ...prev,
                              customStartDate: e.target.value,
                              timePeriod: 'custom',
                            }))
                          }
                          disabled={isRunning}
                        />
                        <span className="report-range__to">to</span>
                        <Form.Control
                          type="date"
                          aria-label="End date"
                          value={parameters.customEndDate}
                          onChange={(e) =>
                            setParameters((prev) => ({
                              ...prev,
                              customEndDate: e.target.value,
                              timePeriod: 'custom',
                            }))
                          }
                          disabled={isRunning}
                        />
                      </div>
                      {parameters.customStartDate && parameters.customEndDate && !isCustomRangeValid ? (
                        <span className="report-field__error">
                          The start date must be the end date or a day before it.
                        </span>
                      ) : (
                        <Form.Text>Both days are included. Days run on New Zealand time.</Form.Text>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="report-range">
                        <Form.Control
                          type="month"
                          aria-label="Start month"
                          value={startMonthValue}
                          onChange={(e) => handleStartMonthChange(e.target.value)}
                          disabled={isRunning}
                          isInvalid={!isStartMonthInputValid}
                        />
                        <span className="report-range__to">to</span>
                        <Form.Control
                          type="month"
                          aria-label="End month"
                          value={endMonthValue}
                          onChange={(e) => handleEndMonthChange(e.target.value)}
                          disabled={isRunning}
                          isInvalid={!isEndMonthInputValid}
                        />
                      </div>
                      <div className="report-quick">
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => setMonthRange(currentMonthValue(), currentMonthValue())}
                          disabled={isRunning}
                        >
                          This month
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => setMonthRange(previousMonthValue(), previousMonthValue())}
                          disabled={isRunning}
                        >
                          Last month
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => setMonthRange(previousMonthValue(), currentMonthValue())}
                          disabled={isRunning}
                        >
                          Last month and this month
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => setMonthRange(`${currentYear}-01`, `${currentYear}-12`)}
                          disabled={isRunning}
                        >
                          This year
                        </Button>
                      </div>
                      {monthValidationError ? (
                        <span className="report-field__error">{monthValidationError}</span>
                      ) : (
                        <Form.Text>Both months are included. Months run on New Zealand time.</Form.Text>
                      )}
                    </>
                  )}
                </Form.Group>

                <Form.Group className="report-field" controlId="usage-format">
                  <Form.Label>File format</Form.Label>
                  <Form.Select
                    value={parameters.outputFormat}
                    onChange={(e) => handleParameterChange('outputFormat', e.target.value as 'csv' | 'json')}
                    disabled={isRunning}
                  >
                    <option value="csv">CSV: one file for each report</option>
                    <option value="json">JSON: one file with every report</option>
                  </Form.Select>
                </Form.Group>
              </div>
            </div>
          </Card.Body>
          <div className="report-form__foot">
            <p className="report-form__hint">
              {blocked ||
                `Reads ${countOf(parameters.clientNames.length, 'client')} for ${getTimePeriodLabel()}, one after the other. More clients and a longer period take longer.`}
            </p>
            <Button variant="primary" onClick={handleExecute} disabled={!isFormValid() || isRunning || loadingClients}>
              Run report
            </Button>
          </div>
        </Card>
      )}

      {execution && (
        <Card className="report-run">
          <Card.Body>
            <ReportChoices
              running={isRunning}
              onChange={handleChangeChoices}
              onStartOver={handleReset}
              choices={[
                { label: 'Clients', value: getClientSelectionText() },
                { label: 'Reports', value: formatReportList(parameters.reports) },
                { label: 'Period', value: getTimePeriodLabel() },
                { label: 'Counted by', value: parameters.timeGranularity === 'day' ? 'Day' : 'Month' },
                { label: 'File format', value: parameters.outputFormat.toUpperCase() },
              ]}
            />
            <div className="report-run__progress">
              <ProgressTracker
                status={execution.status}
                progress={execution.progress}
                error={execution.error}
                startedAt={execution.startedAt}
                completedAt={execution.completedAt}
                onStop={cancel}
              />
            </div>
          </Card.Body>
        </Card>
      )}

      {finished && report && (
        <>
          <p className="report-result__note">
            {countOf(totalRows, 'row')} in {countOf(tables.length, 'report')}, from{' '}
            {countOf(report.metadata.clientsProcessed, 'client')}, for {getTimePeriodLabel()}. Read{' '}
            {formatNzDateTime(report.metadata.exportDate)} New Zealand time.
          </p>

          <ReportStats stats={buildStats(report.metadata)} />

          <ReportFailures failures={failedClients} />

          {resultFiles.length === 0 ? (
            <Alert variant="warning">
              The report found nothing to export for these clients in this period, so there is no file. Change the
              choices to try other clients or a longer period.
            </Alert>
          ) : (
            totalRows === 0 && (
              <Alert variant="warning">
                No activity was found for these clients in this period. The files hold the column headings and no rows.
              </Alert>
            )
          )}

          <Card className="report-card">
            <Card.Header className="report-card__head">
              <h2 className="report-card__title">
                Tables
                {shownTable && (
                  <span className="report-card__sub">
                    {REPORT_OPTIONS.find((option) => option.value === shownTable.key)?.description}
                  </span>
                )}
              </h2>
              <ReportDownloads files={resultFiles} />
            </Card.Header>
            {shownTable ? (
              <>
                <ReportTabs
                  activeKey={shownTable.key}
                  onSelect={setActiveTab}
                  tabs={tables.map((entry) => ({
                    key: entry.key,
                    label: REPORT_LABEL[entry.key],
                    count: entry.count,
                  }))}
                />
                {shownTable.render()}
              </>
            ) : (
              <p className="report-card__body-note usage-no-tables">This run made no tables.</p>
            )}
          </Card>
        </>
      )}
    </ToolPage>
  );
}
